import {
  AccessControlUser,
  mapAccessControlDirectory,
  mapPeerFolders,
  mapQueryAccessFiles,
  mapQueryAccessFolders,
  mapQueryAccessFoldersGroups,
  mapQueryAccessFoldersUsers,
  mapQueryDepartmentFilter,
  mapQueryGetManegarList,
  mutationChangeAccessFoldersReviewState,
  queryAccessControlDirectory,
  queryAccessFiles,
  queryAccessFolders,
  queryAccessFoldersGroups,
  queryAccessFoldersUsers,
  queryDepartmentFilter,
  queryGetManegarList,
  queryPeerFolders
} from './accessFoldersQueries'
import {
  AccessFoldersReviewStatuses,
  AccessFoldersTypes,
  AccessFolderTabs,
  FolderAccessGroupTypes,
  FolderAccessModes,
  UserAccessEmployeeType
} from './pages/folders/types'
import { DataSensitivityLevels } from './types'
import { PAGE, SENSITIVE_LABEL } from '../../constants'
import graphqlService from '../../services/graphqlService'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

export interface AccessControlFoldersParams {
  [PAGE]: number
  datasourceId: string
  parentId?: number
  search?: string
  hasOpenObjects?: boolean
  hasSensitiveObjects?: boolean
  accessTypes?: AccessFoldersTypes[]
  reviewStatuses?: AccessFoldersReviewStatuses[]
  isInherited?: boolean
  ownerIds?: string[]
  hasOpenAccess?: boolean
  hasExternalAccess?: boolean
  hasExcessiveAccess?: boolean
  hasInheritanceIssues?: boolean
  countType?: AccessFolderTabs
  directoryAccessTypeCategory?: AccessFolderTabs
  sensitivity?: SENSITIVE_LABEL[]
  attributes?: string[]
}

export type AccessDirectoryCounts = {
  total: number
  needsReview: number
  sensitive: number
  inheritingPermissions: number
  specificPermissions: number
}

export type AccessControlFolder = {
  id: string
  name: string
  accessTypes: AccessFoldersTypes[]
  groupAccessCount: number
  userAccessCount: number
  owner: { id: string; name: string }
  reviewStatus: AccessFoldersReviewStatuses
  path: string
  inherited: number
  lastModifiedTime: string
  files: AccessDirectoryCounts
  unfilteredFiles: AccessDirectoryCounts
  directFiles: AccessDirectoryCounts
  directFolders: AccessDirectoryCounts
  folders: AccessDirectoryCounts
  objectLink?: string | null
  managers: {
    list?: AccessControlUser[]
  }
  attributeFindings?: Array<{ id: string; name: string; filesCount: number }>
}

export type AccessControlFile = {
  id: string
  fileId: string
  name: string
  accessTypes: AccessFoldersTypes[]
  sensitivityLevel: DataSensitivityLevels
  reviewStatus: AccessFoldersReviewStatuses
  objectLink?: string
  labels?: Array<{ id: string; name: string }>
  attributes?: Array<{ id: string; name: string }>
  accessibleUsersCount: number
  accessibleGroupsCount: number
  lastModifiedTime?: string
  owner?: {
    email: string
  }
}

export const ACTION_ACCESS_FOLDERS = 'accessFolders/folders'
export const fetchAccessControlFolders = createAsyncThunk(
  ACTION_ACCESS_FOLDERS,
  async (params: AccessControlFoldersParams) => {
    const resultRaw = await graphqlService.execute(queryAccessFolders(params))
    return mapQueryAccessFolders(resultRaw)
  }
)

export const ACTION_ACCESS_FILES = 'accessFolders/files'
export const fetchAccessControlFiles = createAsyncThunk(
  ACTION_ACCESS_FILES,
  async (params: AccessControlFoldersParams) => {
    const resultRaw = await graphqlService.execute(queryAccessFiles(params))

    return mapQueryAccessFiles(resultRaw, params.directoryAccessTypeCategory)
  }
)

export type AccessControlDirectory = {
  id: string | null
  name: string
  path: string | null
  inherited: boolean | null
  objectLink: string | null
  groupAccess: {
    count: number
  }
  userAccess: {
    count: number
  }
  owner: {
    name: string
    id: string
  } | null
  lastModifiedTime: string | null
  accessTypes: AccessFoldersTypes[]
  accessControlDirectoryCounts: {
    files: {
      total: number
      needsReview: number
      sensitive: number
    }
    folders: {
      total: number
      needsReview: number
      sensitive: number
    }
  }
  reviewStatus: AccessFoldersReviewStatuses | null
}

export type AccessControlDirectoryParams = {
  folderId: string
  datasourceId: string
  searchQuery: string
  countType?: AccessFolderTabs
}

export const ACTION_FETCH_ACCESS_CONTROL_DIRECTORY = 'accessFolders/accessControlDirectory'
export const fetchAccessControlDirectory = createAsyncThunk(
  ACTION_FETCH_ACCESS_CONTROL_DIRECTORY,
  async (params: AccessControlDirectoryParams) => {
    const resultRaw = await graphqlService.execute(queryAccessControlDirectory(params))

    return mapAccessControlDirectory(resultRaw)
  }
)

export type AccessControlChangeReviewStateParams = {
  ids: number[]
  datasourceId: string
  reviewStatus: AccessFoldersReviewStatuses
  accessType: AccessFolderTabs
}

export const ACTION_ACCESS_FOLDERS_CHANGE_REVIEW_STATE = 'accessFolders/changeReviewState'
export const changeAccessFoldersReviewState = createAsyncThunk(
  ACTION_ACCESS_FOLDERS_CHANGE_REVIEW_STATE,
  async (params: AccessControlChangeReviewStateParams) => {
    return await graphqlService.execute(mutationChangeAccessFoldersReviewState(params))
  }
)

export type AccessControlPeerFolders = {
  id: number
  name: string
}

export type FetchPeerFoldersParams = {
  folderId: number
  datasourceId: string
}
export const ACTION_FETCH_PEER_FOLDERS = 'accessFolders/fetchPeerFolders'
export const fetchPeerFolders = createAsyncThunk(
  ACTION_FETCH_PEER_FOLDERS,
  async (params: FetchPeerFoldersParams) => {
    const resultRaw = await graphqlService.execute(queryPeerFolders(params))

    return mapPeerFolders(resultRaw)
  }
)

//Groups and Users
export interface AccessControlDirectoryGroupsAndUsers {
  id: string
  name: string
  accessModes: FolderAccessModes
  groupsCount: number
  filesCount: number
  directoryCount: number
}
export interface AccessControlDirectoryGroupsAndUsersParams {
  isFoldersView: boolean
  datasourceId: string
  folderId: string
  accessModes?: FolderAccessModes
  departmentName?: string
  employeeType?: string
}

export interface AccessControlDirectoryGroups extends AccessControlDirectoryGroupsAndUsers {
  type: FolderAccessGroupTypes
  usersCount: number
}

export const ACTION_FETCH_ACCESS_FOLDERS_GROUPS = 'accessFolders/fetchGroups'
export const fetchAccessFoldersGroups = createAsyncThunk(
  ACTION_FETCH_ACCESS_FOLDERS_GROUPS,
  async (params: AccessControlDirectoryGroupsAndUsersParams) => {
    const resultRaw = await graphqlService.execute(queryAccessFoldersGroups(params))

    return mapQueryAccessFoldersGroups(resultRaw, params.isFoldersView)
  }
)
export interface AccessControlDirectoryUsers extends AccessControlDirectoryGroupsAndUsers {
  departmentName: string
  employeeType: UserAccessEmployeeType
}

export const ACTION_FETCH_ACCESS_FOLDERS_USERS = 'accessFolders/fetchUsers'
export const fetchAccessFoldersUsers = createAsyncThunk(
  ACTION_FETCH_ACCESS_FOLDERS_USERS,
  async (params: AccessControlDirectoryGroupsAndUsersParams) => {
    const resultRaw = await graphqlService.execute(queryAccessFoldersUsers(params))
    return mapQueryAccessFoldersUsers(resultRaw, params.isFoldersView)
  }
)

export interface DepartmentFilterParams {
  datasourceId: string
}
export interface DepartmentFilter {
  departmentName: string
}
export const ACTION_FETCH_DEPARTMENT_FILTER = 'accessFolders/fetchFilters/departmen'
export const fetchDepartmentFilter = createAsyncThunk(
  ACTION_FETCH_DEPARTMENT_FILTER,
  async (params: DepartmentFilterParams) => {
    const resultRaw = await graphqlService.execute(queryDepartmentFilter(params))
    return mapQueryDepartmentFilter(resultRaw)
  }
)

export const ACTION_FETCH_MANAGER_LIST = 'accessControl/managersList'
export const fetchManagerList = createAsyncThunk(
  ACTION_FETCH_MANAGER_LIST,
  async (params: DepartmentFilterParams) => {
    const resultRaw = await graphqlService.execute(queryGetManegarList(params))
    return mapQueryGetManegarList(resultRaw)
  }
)

interface AccessControlFoldersState {
  folders?: AccessControlFolder[]
  foldersCount?: number
  accessControlDirectory?: AccessControlDirectory
  files?: AccessControlFile[]
  filesCount?: number
  peerFolders?: AccessControlPeerFolders[]
  groups?: AccessControlDirectoryGroups[]
  groupsCount?: number
  users?: AccessControlDirectoryUsers[]
  usersCount?: number
  departments?: DepartmentFilter[]
  managers: {
    list?: AccessControlUser[]
  }
}
const initialState: AccessControlFoldersState = {
  managers: {
    list: []
  }
}

const accessControlFoldersSlice = createSlice({
  name: 'accessFolders',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchAccessControlFolders.fulfilled, (state, { payload }) => {
      state.folders = payload.list
      state.foldersCount = payload.total
    })
    builder.addCase(fetchAccessControlFiles.fulfilled, (state, { payload }) => {
      state.files = payload.list?.map((file) => ({ ...file, id: file.id || file.fileId }))
      state.filesCount = payload.count
    })
    builder.addCase(fetchPeerFolders.fulfilled, (state, { payload }) => {
      state.peerFolders = payload
    })
    builder.addCase(fetchAccessControlDirectory.fulfilled, (state, { payload }) => {
      state.accessControlDirectory = payload
    })
    builder.addCase(fetchAccessFoldersGroups.fulfilled, (state, { payload }) => {
      state.groups = payload.list
      state.groupsCount = payload.total
    })
    builder.addCase(fetchAccessFoldersUsers.fulfilled, (state, { payload }) => {
      state.users = payload.list
      state.usersCount = payload.total
    })
    builder.addCase(fetchDepartmentFilter.fulfilled, (state, { payload }) => {
      state.departments = payload.list
    })
    builder.addCase(fetchManagerList.fulfilled, (state, { payload }) => {
      state.managers.list = payload.list as AccessControlUser[]
    })
    builder.addCase(fetchManagerList.rejected, (state) => {
      state.managers.list = []
    })
  }
})

export default accessControlFoldersSlice.reducer
